import React, { useEffect, useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { Play, Pause, Volume2, VolumeX } from 'lucide-react';
export function AudioPlayer() {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [showVolumeSlider, setShowVolumeSlider] = useState(false);
  const audioRef = useRef<HTMLAudioElement>(null);
  // Convert Dropbox URL to direct download link
  const audioUrl = 'https://www.dropbox.com/scl/fi/vknp0gwwnv80hk5s386fb/Previa-AudioBook-DivorcioEmocional-MP3.mp3?rlkey=fm2p2eymz5cuwief26l6t1wmy&st=faouoiwz&dl=1';
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const updateTime = () => setCurrentTime(audio.currentTime);
    const updateDuration = () => setDuration(audio.duration);
    const handleEnded = () => setIsPlaying(false);
    audio.addEventListener('timeupdate', updateTime);
    audio.addEventListener('loadedmetadata', updateDuration);
    audio.addEventListener('ended', handleEnded);
    return () => {
      audio.removeEventListener('timeupdate', updateTime);
      audio.removeEventListener('loadedmetadata', updateDuration);
      audio.removeEventListener('ended', handleEnded);
    };
  }, []);
  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (isPlaying) {
      audio.pause();
    } else {
      audio.play();
    }
    setIsPlaying(!isPlaying);
  };
  const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
    const audio = audioRef.current;
    if (!audio) return;
    const newTime = parseFloat(e.target.value);
    audio.currentTime = newTime;
    setCurrentTime(newTime);
  };
  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const audio = audioRef.current;
    if (!audio) return;
    const newVolume = parseFloat(e.target.value);
    audio.volume = newVolume;
    setVolume(newVolume);
    setIsMuted(newVolume === 0);
  };
  const toggleMute = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (isMuted) {
      audio.volume = volume || 0.5;
      setIsMuted(false);
    } else {
      audio.volume = 0;
      setIsMuted(true);
    }
  };
  const formatTime = (time: number) => {
    if (isNaN(time)) return '0:00';
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };
  const progress = duration > 0 ? currentTime / duration * 100 : 0;
  return <>
      <audio ref={audioRef} src={audioUrl} preload="metadata" data-id="element-6" />

      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.5,
      duration: 0.6
    }} className="w-full max-w-xl mx-auto" data-id="element-7">
        {/* Title */}
        <h3 className="text-center text-[#2d2d2d] font-medium mb-3 text-base md:text-lg" data-id="element-8">
          Escucha una Introducción del eBook
        </h3>

        {/* Player Container */}
        <div className="bg-[#2d2d2d] rounded-2xl px-6 py-4 shadow-xl" data-id="element-9">
          <div className="flex items-center gap-4" data-id="element-10">
            {/* Play/Pause Button */}
            <motion.button whileHover={{
            scale: 1.05
          }} whileTap={{
            scale: 0.95
          }} onClick={togglePlay} className="w-10 h-10 flex-shrink-0 bg-[#9d4b6b] hover:bg-[#8b6f6f] text-white rounded-lg flex items-center justify-center transition-colors shadow-md" aria-label={isPlaying ? 'Pause' : 'Play'} data-id="element-11">
              {isPlaying ? <Pause className="w-5 h-5" fill="currentColor" data-id="element-12" /> : <Play className="w-5 h-5 ml-0.5" fill="currentColor" data-id="element-13" />}
            </motion.button>

            {/* Time Display */}
            <div className="flex items-center gap-2 text-white/70 text-sm font-mono flex-shrink-0" data-id="element-14">
              <span data-id="element-15">{formatTime(currentTime)}</span>
              <span data-id="element-16">/</span>
              <span data-id="element-17">{formatTime(duration)}</span>
            </div>

            {/* Progress Bar */}
            <div className="flex-1 min-w-0" data-id="element-18">
              <input type="range" min="0" max={duration || 0} value={currentTime} onChange={handleSeek} className="w-full h-1.5 bg-white/20 rounded-full appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-3 [&::-webkit-slider-thumb]:h-3 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-[#9d4b6b] [&::-webkit-slider-thumb]:cursor-pointer [&::-webkit-slider-thumb]:shadow-md [&::-webkit-slider-thumb]:hover:bg-[#8b6f6f] [&::-webkit-slider-thumb]:transition-colors [&::-moz-range-thumb]:w-3 [&::-moz-range-thumb]:h-3 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-[#9d4b6b] [&::-moz-range-thumb]:border-0 [&::-moz-range-thumb]:cursor-pointer [&::-moz-range-thumb]:hover:bg-[#8b6f6f]" style={{
              background: `linear-gradient(to right, #9d4b6b ${progress}%, rgba(255,255,255,0.2) ${progress}%)`
            }} data-id="element-19" />
            </div>

            {/* Volume Control */}
            <div className="relative flex-shrink-0" onMouseEnter={() => setShowVolumeSlider(true)} onMouseLeave={() => setShowVolumeSlider(false)} data-id="element-20">
              <button onClick={toggleMute} className="w-10 h-10 flex items-center justify-center text-white/70 hover:text-white transition-colors rounded-lg hover:bg-white/5" aria-label={isMuted ? 'Unmute' : 'Mute'} data-id="element-21">
                {isMuted ? <VolumeX className="w-5 h-5" data-id="element-22" /> : <Volume2 className="w-5 h-5" data-id="element-23" />}
              </button>

              {/* Volume Slider (appears on hover) */}
              {showVolumeSlider && <motion.div initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: 10
            }} className="absolute bottom-full right-0 mb-2 bg-[#1a1a1a] rounded-lg p-3 shadow-xl" data-id="element-24">
                  <input type="range" min="0" max="1" step="0.01" value={isMuted ? 0 : volume} onChange={handleVolumeChange} className="w-24 h-1.5 bg-white/20 rounded-full appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-3 [&::-webkit-slider-thumb]:h-3 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-[#9d4b6b] [&::-webkit-slider-thumb]:cursor-pointer [&::-moz-range-thumb]:w-3 [&::-moz-range-thumb]:h-3 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-[#9d4b6b] [&::-moz-range-thumb]:border-0 [&::-moz-range-thumb]:cursor-pointer" aria-label="Volume" data-id="element-25" />
                </motion.div>}
            </div>
          </div>
        </div>

        {/* Subtitle */}
        <p className="text-center text-[#9d4b6b] text-sm md:text-base mt-3 font-bold italic" data-id="element-26">
          ¡Tu primer paso empieza Aquí!
        </p>
      </motion.div>
    </>;
}